import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, QrCode, ArrowLeft } from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCode className="w-8 h-8 text-primary" />
              <span className="text-xl font-bold font-['Manrope']">IziCatalog</span>
            </Link>
            
            <div className="flex items-center space-x-4">
              <Link to="/login" className="text-muted-foreground hover:text-foreground transition-colors">
                Connexion
              </Link>
              <Button onClick={() => navigate('/register')} className="rounded-full">
                Commencer
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Back button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </div>

      {/* Pricing */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold font-['Manrope'] mb-4">
              Choisissez votre plan
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Des tarifs simples et transparents. Sans engagement, résiliable à tout moment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Starter */}
            <PlanCard
              id="STARTER"
              name="Starter"
              price={19}
              description="Pour démarrer"
              features={[
                "1 boutique",
                "3 catégories maximum",
                "30 produits maximum",
                "QR code du catalogue",
                "Page publique du catalogue",
                "Support par email"
              ]}
              onSelect={() => navigate('/register')}
            />

            {/* Pro */}
            <PlanCard
              id="PRO"
              name="Pro"
              price={39}
              description="Le plus populaire"
              recommended
              features={[
                "1 boutique",
                "Categories illimitees",
                "Produits illimites",
                "Themes predefinis",
                "Couleurs personnalisables",
                "Produits mis en avant",
                "Statistiques detaillees",
                "Visites, clics WhatsApp, produits populaires",
                "Support prioritaire"
              ]}
              onSelect={() => navigate('/register')}
            />

            {/* Premium */}
            <PlanCard
              id="PREMIUM"
              name="Premium"
              price={59}
              description="Pour les pros"
              features={[
                "Tout le plan Pro",
                "2 boutiques",
                "Multi-utilisateurs (STAFF)",
                "QR code par catégorie",
                "Export PDF du catalogue complet",
                "Import CSV de produits",
                "IA pour générer des descriptions",
                "IA pour corriger les fautes",
                "IA pour suggérer des catégories",
                "Support dédié"
              ]}
              onSelect={() => navigate('/register')}
            />
          </div>

          {/* FAQ */}
          <div className="mt-24 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold font-['Manrope'] text-center mb-12">
              Questions fréquentes
            </h2>
            
            <div className="space-y-6">
              <FaqItem 
                question="Puis-je changer de plan à tout moment ?"
                answer="Oui, vous pouvez upgrader ou downgrader votre plan à tout moment. Les changements prennent effet immédiatement."
              />
              <FaqItem 
                question="Y a-t-il un engagement ?"
                answer="Non, tous nos plans sont sans engagement. Vous pouvez résilier à tout moment."
              />
              <FaqItem 
                question="Comment fonctionne le paiement ?"
                answer="Le paiement est mensuel et se fait par carte bancaire. Vous êtes facturé au début de chaque période."
              />
              <FaqItem 
                question="Puis-je essayer avant de payer ?"
                answer="Oui, vous pouvez créer votre catalogue et le tester. Le paiement n'est requis que pour publier votre catalogue."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-muted-foreground text-sm">
            © 2024 IziCatalog. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
};

const PlanCard = ({ id, name, price, description, features, recommended, onSelect }) => {
  return (
    <div 
      className={`relative p-8 bg-card rounded-2xl border-2 transition-all duration-300 ${
        recommended 
          ? 'border-primary shadow-xl scale-105 z-10' 
          : 'border-border hover:border-primary/50 hover:shadow-lg'
      }`}
      data-testid={`plan-card-${id.toLowerCase()}`}
    >
      {recommended && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-primary text-primary-foreground px-4 py-1.5 rounded-full text-sm font-semibold">
            Recommandé
          </span>
        </div>
      )}
      
      <div className="mb-6">
        <h3 className="text-2xl font-bold mb-1">{name}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>

      <div className="mb-8">
        <span className="text-5xl font-bold">{price}€</span>
        <span className="text-muted-foreground">/mois</span>
      </div>
      
      <ul className="space-y-4 mb-8">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-primary" />
            </div>
            <span className="text-sm">{feature}</span>
          </li>
        ))}
      </ul>
      
      <Button 
        className={`w-full rounded-full ${recommended ? 'h-12 text-lg' : ''}`}
        variant={recommended ? 'default' : 'outline'}
        size={recommended ? 'lg' : 'default'}
        onClick={onSelect}
        data-testid={`pricing-select-${id.toLowerCase()}`}
      >
        Choisir {name}
      </Button>
    </div>
  );
};

const FaqItem = ({ question, answer }) => {
  return (
    <div className="p-6 bg-muted/50 rounded-xl">
      <h3 className="font-semibold mb-2">{question}</h3>
      <p className="text-muted-foreground text-sm">{answer}</p>
    </div>
  );
};

export default PricingPage;
